import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { CodyIcon } from '../components/CodyLogo';
import { ArrowLeft, Crown, Building2, Zap, Key, RefreshCw, Lock, Unlock, Trash2, FileText, Send, CreditCard, DollarSign, Gift, Clock, XCircle } from 'lucide-react';

const PC = { free: { bg: '#F6F7F9', fg: '#5B667A', label: 'Startup' }, pro: { bg: '#FFF2EA', fg: '#FF4500', label: 'Growth' }, agency: { bg: '#EFF6FF', fg: '#2563EB', label: 'Agency' } };
function PlanBadge({ plan }) { const p = PC[plan] || PC.free; return <Badge className="text-[10px] font-semibold" style={{ background: p.bg, color: p.fg }}>{p.label}</Badge>; }

export default function OpsUserDetail() {
  const { userId } = useParams();
  const { user: admin } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  const [planChange, setPlanChange] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const [billingNote, setBillingNote] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Billing action form
  const [actionType, setActionType] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [actionMonths, setActionMonths] = useState('1');
  const [actionAmount, setActionAmount] = useState('');
  const [actionPlan, setActionPlan] = useState('pro');

  useEffect(() => {
    if (admin?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchAll();
  }, [admin, userId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [d, b] = await Promise.all([api.getAdminUser(userId), api.getUserBilling(userId)]);
      setDetail(d); setBilling(b);
      setPlanChange(d.plan || 'free'); setBillingCycle(d.billing_cycle || 'monthly');
    } catch { toast.error('Failed to load user'); navigate('/ops'); }
    finally { setLoading(false); }
  };

  const handlePlanUpdate = async () => {
    try { await api.updateUserPlan(userId, { plan: planChange, billing_cycle: billingCycle, billing_note: billingNote || undefined }); toast.success('Plan updated'); setBillingNote(''); fetchAll(); } catch (e) { toast.error(e.message); }
  };
  const handleSuspend = async () => { try { const r = await api.suspendUser(userId); toast.success(r.is_suspended ? 'Suspended' : 'Unsuspended'); fetchAll(); } catch (e) { toast.error(e.message); } };
  const handleResetPassword = async () => { if (!newPassword || newPassword.length < 6) { toast.error('Min 6 chars'); return; } try { await api.resetUserPassword(userId, newPassword); toast.success('Password reset'); setNewPassword(''); fetchAll(); } catch (e) { toast.error(e.message); } };
  const handleResetAi = async () => { try { await api.resetUserAiReplies(userId); toast.success('AI replies reset'); fetchAll(); } catch (e) { toast.error(e.message); } };
  const handleAddNote = async () => { if (!newNote.trim()) return; try { await api.addAdminNote(userId, newNote.trim()); toast.success('Note added'); setNewNote(''); fetchAll(); } catch (e) { toast.error(e.message); } };
  const handleDelete = async () => { try { await api.deleteUser(userId); toast.success('User deleted'); navigate('/ops'); } catch (e) { toast.error(e.message); } };

  const handleBillingAction = async () => {
    if (!actionType || !actionNote) { toast.error('Select action and add a note'); return; }
    try {
      await api.userBillingAction(userId, { action: actionType, note: actionNote, months: parseInt(actionMonths), amount: parseFloat(actionAmount) || 0, plan: actionPlan });
      toast.success('Billing action applied');
      setActionType(''); setActionNote(''); setActionMonths('1'); setActionAmount('');
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA]"><div className="max-w-6xl mx-auto px-6 py-8 space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div></div>;
  if (!detail) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="bg-white border-b border-[#E6E8EE] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" className="text-xs text-[#5B667A]" onClick={() => navigate('/ops')}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Ops</Button>
          <div className="flex items-center gap-2"><CodyIcon size={28} /><span className="text-xs text-[#9ca3af]">Ops Panel</span></div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-extrabold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>{detail.email}</h1>
            <div className="flex items-center gap-3">
              <PlanBadge plan={detail.plan} />
              <span className="text-xs text-[#5B667A] capitalize">{(detail.plan || 'free') === 'free' ? 'Free' : (detail.billing_cycle || 'monthly')}</span>
              {detail.is_suspended && <Badge variant="destructive" className="text-[10px]">Suspended</Badge>}
              {billing?.is_comped && <Badge className="text-[10px] bg-[#F0FDF4] text-[#16A34A]">Comped</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className={`text-xs ${detail.is_suspended ? 'text-[#16A34A]' : 'text-red-500'}`} onClick={handleSuspend}>
              {detail.is_suspended ? <><Unlock className="w-3.5 h-3.5 mr-1.5" /> Unsuspend</> : <><Lock className="w-3.5 h-3.5 mr-1.5" /> Suspend</>}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          {[
            { l: 'Brands', v: detail.businesses?.length || 0 },
            { l: 'Total Leads', v: detail.businesses?.reduce((a, b) => a + (b.leads_count || 0), 0) || 0 },
            { l: 'AI Replies', v: detail.ai_replies_this_month || 0 },
            { l: 'Lifetime Value', v: `$${billing?.lifetime_value || 0}` },
            { l: 'Transactions', v: billing?.total_transactions || 0 },
          ].map((s, i) => (
            <Card key={i} className="p-4 rounded-xl border-[#E6E8EE] bg-white text-center">
              <p className="text-xl font-extrabold tabular-nums" style={{ fontFamily: "'Space Grotesk'" }}>{s.v}</p>
              <p className="text-[10px] text-[#5B667A] mt-1">{s.l}</p>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          <div className="space-y-6">
            {/* Plan & Billing */}
            <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white">
              <h3 className="text-base font-bold mb-5 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk'" }}><Crown className="w-5 h-5 text-[#FF4500]" /> Plan & Billing</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div><label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Plan</label><Select value={planChange} onValueChange={setPlanChange}><SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="free">Startup (Free)</SelectItem><SelectItem value="pro">Growth ($49/mo)</SelectItem><SelectItem value="agency">Agency ($99/mo)</SelectItem></SelectContent></Select></div>
                <div><label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Billing Cycle</label><Select value={billingCycle} onValueChange={setBillingCycle}><SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent></Select></div>
              </div>
              <div className="mb-4"><label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Note</label><Input placeholder="e.g. Stripe #xyz, comped, etc." value={billingNote} onChange={(e) => setBillingNote(e.target.value)} className="h-10 text-sm" /></div>
              <Button className="h-10 px-6 rounded-lg text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }} onClick={handlePlanUpdate}>Save Plan Changes</Button>
            </Card>

            {/* Billing Actions */}
            <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white">
              <h3 className="text-base font-bold mb-5 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk'" }}><CreditCard className="w-5 h-5 text-[#16A34A]" /> Billing Actions</h3>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {[
                  { id: 'comp', label: 'Comp Plan', icon: Gift, desc: 'Give free access for X months', color: '#16A34A' },
                  { id: 'extend', label: 'Extend Sub', icon: Clock, desc: 'Add months to current sub', color: '#2563EB' },
                  { id: 'credit', label: 'Apply Credit', icon: DollarSign, desc: 'Add $ credit to account', color: '#D97706' },
                  { id: 'cancel', label: 'Cancel Plan', icon: XCircle, desc: 'Downgrade to free', color: '#EF4444' },
                ].map(a => (
                  <button key={a.id} onClick={() => setActionType(a.id)}
                    className={`p-3 rounded-lg border text-left transition-all ${actionType === a.id ? 'border-[#FF4500] bg-[#FFF8F5]' : 'border-[#E6E8EE] hover:border-[#d1d5db]'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <a.icon className="w-4 h-4" style={{ color: a.color }} />
                      <span className="text-sm font-semibold text-[#0B0F17]">{a.label}</span>
                    </div>
                    <p className="text-[10px] text-[#5B667A]">{a.desc}</p>
                  </button>
                ))}
              </div>
              {actionType && (
                <div className="p-4 rounded-lg bg-[#F9FAFB] border border-[#E6E8EE] space-y-3">
                  <p className="text-xs font-bold text-[#0B0F17] capitalize">{actionType.replace('_', ' ')} action</p>
                  {actionType === 'comp' && (
                    <>
                      <div>
                        <label className="text-[10px] text-[#5B667A] block mb-1">Plan Type</label>
                        <Select value={actionPlan} onValueChange={setActionPlan}>
                          <SelectTrigger className="h-8 text-sm w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pro">Growth</SelectItem>
                            <SelectItem value="agency">Agency</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] text-[#5B667A] block mb-1">Months <span className="text-[#9ca3af]">(0 = lifetime)</span></label>
                        <Input type="number" min="0" max="36" value={actionMonths} onChange={(e) => setActionMonths(e.target.value)} className="h-8 text-sm w-24" />
                      </div>
                    </>
                  )}
                  {actionType === 'extend' && (
                    <div><label className="text-[10px] text-[#5B667A] block mb-1">Months</label><Input type="number" min="1" max="36" value={actionMonths} onChange={(e) => setActionMonths(e.target.value)} className="h-8 text-sm w-24" /></div>
                  )}
                  {actionType === 'credit' && (
                    <div><label className="text-[10px] text-[#5B667A] block mb-1">Amount ($)</label><Input type="number" min="0" step="0.01" value={actionAmount} onChange={(e) => setActionAmount(e.target.value)} placeholder="0.00" className="h-8 text-sm w-32" /></div>
                  )}
                  <div><label className="text-[10px] text-[#5B667A] block mb-1">Reason (required)</label><Input value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="Reason for this action..." className="h-8 text-sm" /></div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-8 text-xs" style={{ background: 'linear-gradient(135deg, #FF6A2A, #FF4500)' }} onClick={handleBillingAction}>Apply</Button>
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setActionType(''); setActionNote(''); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Payment History */}
            <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white">
              <h3 className="text-base font-bold mb-5 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk'" }}><DollarSign className="w-5 h-5 text-[#D97706]" /> Payment History</h3>
              {billing?.transactions?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-[#E6E8EE]">
                      <th className="text-left text-[10px] font-semibold text-[#5B667A] uppercase py-2 pr-4">Date</th>
                      <th className="text-left text-[10px] font-semibold text-[#5B667A] uppercase py-2 pr-4">Package</th>
                      <th className="text-right text-[10px] font-semibold text-[#5B667A] uppercase py-2 pr-4">Amount</th>
                      <th className="text-center text-[10px] font-semibold text-[#5B667A] uppercase py-2">Status</th>
                    </tr></thead>
                    <tbody>
                      {billing.transactions.map((t, i) => (
                        <tr key={i} className="border-b border-[#E6E8EE]/50 last:border-b-0">
                          <td className="py-2.5 pr-4 text-xs text-[#0B0F17]">{t.date ? new Date(t.date).toLocaleDateString() : '—'}</td>
                          <td className="py-2.5 pr-4"><span className="text-xs capitalize">{(t.plan || '') + ' ' + (t.billing_cycle || '')}</span></td>
                          <td className="py-2.5 pr-4 text-right text-xs font-bold tabular-nums">${t.amount}</td>
                          <td className="py-2.5 text-center">
                            <Badge className={`text-[9px] ${t.payment_status === 'paid' ? 'bg-[#E9F9EF] text-[#16A34A]' : 'bg-[#FFF7E6] text-[#D97706]'}`}>{t.payment_status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-[#9ca3af]">No payment history</p>
              )}
            </Card>

            {/* Businesses */}
            <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white">
              <h3 className="text-base font-bold mb-5 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk'" }}><Building2 className="w-5 h-5 text-[#2563EB]" /> Businesses ({detail.businesses?.length || 0})</h3>
              {detail.businesses?.length === 0 ? <p className="text-sm text-[#9ca3af]">No businesses</p> : (
                <div className="space-y-3">{detail.businesses?.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-4 rounded-lg bg-[#F9FAFB] border border-[#E6E8EE]/60">
                    <div><p className="text-sm font-semibold text-[#0B0F17]">{b.name}</p><p className="text-xs text-[#9ca3af]">{b.website_url}</p>{b.last_auto_scan && <p className="text-[10px] text-[#9ca3af] mt-1">Last scan: {new Date(b.last_auto_scan).toLocaleString()}</p>}</div>
                    <div className="flex gap-4 text-xs text-[#5B667A]">
                      <div className="text-center"><p className="font-bold tabular-nums">{b.keywords_count}</p><p className="text-[10px] text-[#9ca3af]">KW</p></div>
                      <div className="text-center"><p className="font-bold tabular-nums">{b.subreddits_count}</p><p className="text-[10px] text-[#9ca3af]">Subs</p></div>
                      <div className="text-center"><p className="font-bold tabular-nums">{b.leads_count}</p><p className="text-[10px] text-[#9ca3af]">Leads</p></div>
                    </div>
                  </div>
                ))}</div>
              )}
            </Card>

            {/* Notes */}
            <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white">
              <h3 className="text-base font-bold mb-5 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk'" }}><FileText className="w-5 h-5 text-[#16A34A]" /> Internal Notes</h3>
              <div className="flex gap-2 mb-4"><Textarea placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={2} className="text-sm resize-none flex-1" /><Button variant="outline" className="h-auto self-end px-4" onClick={handleAddNote} disabled={!newNote.trim()}><Send className="w-4 h-4" /></Button></div>
              {detail.notes?.length > 0 ? (
                <div className="space-y-3">{detail.notes.map((n, i) => (
                  <div key={n._id || i} className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E6E8EE]/60">
                    <div className="flex items-center justify-between mb-1.5"><Badge variant="secondary" className="text-[10px] capitalize">{n.type}</Badge><span className="text-[10px] text-[#9ca3af]">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span></div>
                    <p className="text-sm text-[#0B0F17]">{n.note}</p><p className="text-[10px] text-[#9ca3af] mt-1">by {n.admin_email}</p>
                  </div>
                ))}</div>
              ) : <p className="text-sm text-[#9ca3af]">No notes yet.</p>}
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white">
              <h3 className="text-base font-bold mb-5 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk'" }}><Zap className="w-5 h-5 text-[#D97706]" /> Quick Actions</h3>
              <div className="space-y-3">
                <Button variant="outline" className={`w-full justify-start h-10 text-sm ${detail.is_suspended ? 'text-[#16A34A]' : 'text-red-500'}`} onClick={handleSuspend}>
                  {detail.is_suspended ? <><Unlock className="w-4 h-4 mr-2" /> Unsuspend</> : <><Lock className="w-4 h-4 mr-2" /> Suspend</>}
                </Button>
                <Button variant="outline" className="w-full justify-start h-10 text-sm" onClick={handleResetAi}><RefreshCw className="w-4 h-4 mr-2" /> Reset AI Counter ({detail.ai_replies_this_month})</Button>
                <div><label className="text-xs font-medium text-[#5B667A] mb-1.5 block">Reset Password</label><div className="flex gap-2"><Input placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="h-10 text-sm flex-1" type="password" /><Button variant="outline" className="h-10 text-sm" onClick={handleResetPassword} disabled={!newPassword}><Key className="w-4 h-4 mr-1" /> Reset</Button></div></div>
              </div>
            </Card>

            <Card className="p-6 rounded-xl border-[#E6E8EE] bg-white">
              <h3 className="text-sm font-bold mb-4" style={{ fontFamily: "'Space Grotesk'" }}>Account Details</h3>
              <div className="space-y-2.5 text-sm">
                {[
                  ['Email', detail.email], ['Role', detail.role], ['Plan', (PC[detail.plan] || PC.free).label],
                  ['Billing', (detail.plan || 'free') === 'free' ? 'Free' : (detail.billing_cycle || 'monthly')],
                  ['Comped', billing?.is_comped ? 'Yes' : 'No'],
                  ['Comp Expires', billing?.comp_expires ? new Date(billing.comp_expires).toLocaleDateString() : '—'],
                  ['Credit Balance', `$${billing?.credit_balance || 0}`],
                  ['Lifetime Value', `$${billing?.lifetime_value || 0}`],
                  ['Status', detail.is_suspended ? 'Suspended' : 'Active'],
                  ['Joined', detail.created_at ? new Date(detail.created_at).toLocaleDateString() : '—'],
                ].map(([k, v], i) => (
                  <div key={i} className="flex justify-between py-1.5 border-b border-[#E6E8EE]/60 last:border-b-0">
                    <span className="text-[#5B667A]">{k}</span><span className="font-medium text-[#0B0F17] capitalize">{v}</span>
                  </div>
                ))}
              </div>
            </Card>

            {detail.role !== 'admin' && (
              <Card className="p-6 rounded-xl border-red-200 bg-red-50/50">
                <h3 className="text-sm font-bold mb-3 text-red-600" style={{ fontFamily: "'Space Grotesk'" }}>Danger Zone</h3>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" className="w-full h-10 text-sm text-red-500 border-red-200 hover:bg-red-100"><Trash2 className="w-4 h-4 mr-2" /> Delete User & All Data</Button></AlertDialogTrigger>
                  <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete {detail.email}?</AlertDialogTitle><AlertDialogDescription>Permanently deletes everything. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={handleDelete}>Delete Everything</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                </AlertDialog>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
